import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useApp } from "../../context/App";
import { api, urls } from "../../services/api";
import { TitlePage } from "../../components/ui/TitlePage";
import { Folder, ChevronLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { ShareSection } from "../../components/ui/ShareSection";

interface IDirectory {
    categoria: number;
    titulo: string;
    descricao?: string;
}

export function DirectoryScreen() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getTenantUrl, mobile } = useApp();
    const [directories, setDirectories] = useState<IDirectory[]>([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState("Listagem");

    useEffect(() => {
        (async () => {
            try {
                const response = await api.get(urls.CategoriaDownload + id);

                if (Array.isArray(response.data)) {
                    setDirectories(response.data);
                }
                else if (response.data.categorias) {
                    setDirectories(response.data.categorias);
                    setTitle(response.data.titulo || "Listagem");
                }
            } catch (error) {
                // error silent
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    return (
        <div className="flex flex-col w-full min-h-screen bg-white dark:bg-surface-dark transition-colors items-center justify-start pb-20">
            <Helmet>
                <title>{title} | Município de Itajaí</title>
            </Helmet>

            {mobile && <div className="h-20" />}

            <div className="w-11/12 lg:w-3/5 mt-8">
                {/* Header Actions */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1.5 text-on-surface-subtle dark:text-on-surface-dark-muted hover:text-primary dark:hover:text-secondary transition-colors font-bold uppercase text-[10px] tracking-widest border-none bg-transparent cursor-pointer"
                    >
                        <ChevronLeft size={16} strokeWidth={3} />
                        Voltar
                    </button>
                </div>

                <TitlePage title={title.toUpperCase()} />

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-4 animate-pulse">
                        <Folder className="text-secondary opacity-20" size={48} />
                        <span className="text-on-surface-subtle font-medium uppercase tracking-widest text-xs">Carregando diretórios...</span>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                            {directories.length > 0 ? (
                                directories.map((dir) => (
                                    <Link
                                        key={dir.categoria}
                                        to={getTenantUrl(`/diretorio/${dir.categoria}`)}
                                        className="group flex flex-col bg-surface-alt/40 dark:bg-surface-dark-alt/40 hover:bg-white dark:hover:bg-surface-dark-alt rounded-3xl p-6 border border-border-default/50 dark:border-border-default-dark/50 hover:border-secondary dark:hover:border-secondary transition-all duration-300 no-underline shadow-sm hover:shadow-xl hover:-translate-y-1"
                                    >
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white dark:bg-surface-dark shadow-inner group-hover:bg-secondary group-hover:text-primary transition-colors duration-300">
                                                <Folder size={28} className="text-secondary group-hover:text-primary transition-colors duration-300" />
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[9px] font-black text-on-surface-subtle/50 dark:text-on-surface-dark-muted/50 uppercase tracking-tighter">
                                                ID: {dir.categoria}
                                            </div>
                                        </div>

                                        <h3 className="text-sm lg:text-base font-bold text-primary dark:text-on-surface-dark leading-tight group-hover:text-secondary transition-colors line-clamp-2 uppercase tracking-tight">
                                            {dir.titulo}
                                        </h3>

                                        <div className="mt-6 flex items-center justify-between">
                                            <span className="text-[10px] font-black text-secondary uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">
                                                Abrir Pasta
                                            </span>
                                            <ChevronLeft className="rotate-180 text-secondary translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" size={18} strokeWidth={3} />
                                        </div>
                                    </Link>
                                ))
                            ) : (
                                <div className="col-span-full flex flex-col items-center py-20 gap-4">
                                    <Folder className="text-border-default dark:text-border-default-dark" size={48} />
                                    <span className="text-on-surface-subtle font-bold uppercase tracking-widest text-xs">Nenhum diretório encontrado.</span>
                                </div>
                            )}
                        </div>

                        {/* Compartilhar */}
                        <ShareSection
                            title="Compartilhar"
                            subtitle="Envie este diretório para alguém"
                        />
                    </>
                )}
            </div>
        </div>
    );
}
